import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const errorOf = (e, fallback) => e?.response?.data?.error || fallback;

/**
 * Settings' plain lists: Departments and Ticket categories (a name and a
 * description each, the same shape), and the built-in Priorities & statuses.
 *
 * Lifted out of the one Settings.js that held every tab, unchanged in what
 * they do. Two things are different: removing one is asked in its own card
 * rather than in a window.confirm() over the page, and the words say what
 * the server does - it hides the row (active = false), it does not delete
 * it, so tickets filed under it keep it.
 */
function NamedList({ path, noun, nouns, placeholder }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [editing, setEditing] = useState(null);
  const [asking, setAsking] = useState(null);
  const [problem, setProblem] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/${path}`);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setProblem(errorOf(e, `Could not load the ${nouns}.`));
      setRows((x) => x || []);
    }
  }, [path, nouns]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setProblem(`A ${noun} needs a name.`); return; }
    setBusy(true);
    setProblem(null);
    try {
      if (editing) await axios.put(`${API}/${path}/${editing.id}`, form);
      else await axios.post(`${API}/${path}`, form);
      setDone(`${form.name.trim()} ${editing ? 'saved' : 'added'}.`);
      setForm({ name: '', description: '' });
      setEditing(null);
      await load();
    } catch (err) {
      setProblem(errorOf(err, `Could not save that ${noun}.`));
    } finally { setBusy(false); }
  };

  const remove = async (row) => {
    setAsking(null);
    try {
      await axios.delete(`${API}/${path}/${row.id}`);
      setDone(`${row.name} removed.`);
      if (editing && editing.id === row.id) { setEditing(null); setForm({ name: '', description: '' }); }
      await load();
    } catch (err) { setProblem(errorOf(err, `Could not remove that ${noun}.`)); }
  };

  const formId = `stn-${path}`;
  return (
    <div className="stn-list">
      {problem && (
        <div className="stn-banner is-problem" role="alert">
          <span>{problem}</span>
          <button type="button" className="btn btn-small btn-secondary" onClick={() => setProblem(null)}>Dismiss</button>
        </div>
      )}
      <p className="stn-flash" role="status" aria-live="polite">{done || ''}</p>
      <section className="stn-card" aria-labelledby={`${formId}-h`}>
        <h3 className="stn-card-title" id={`${formId}-h`}>{editing ? `Edit ${editing.name}` : `Add a ${noun}`}</h3>
        <form className="stn-form" onSubmit={save} noValidate>
          <div className="stn-form-grid">
            <div className="stn-field">
              <label htmlFor={`${formId}-name`}>Name <span className="cfm-req" aria-hidden="true">*</span></label>
              <input id={`${formId}-name`} value={form.name} placeholder={placeholder}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="stn-field stn-field-grow">
              <label htmlFor={`${formId}-desc`}>Description</label>
              <input id={`${formId}-desc`} value={form.description || ''}
                     onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <div className="stn-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>{editing ? 'Save changes' : `Add ${noun}`}</button>
            {editing && (
              <button type="button" className="btn btn-secondary"
                      onClick={() => { setEditing(null); setForm({ name: '', description: '' }); }}>Cancel</button>
            )}
          </div>
        </form>
      </section>

      <ul className="stn-grid" id="s-list" aria-label={nouns}>
        {rows === null && <li className="stn-quiet">Loading…</li>}
        {rows && rows.length === 0 && <li className="stn-quiet">None yet.</li>}
        {(rows || []).map((row) => (
          <li key={row.id} className="stn-item">
            <h4 className="stn-item-name">{row.name}</h4>
            <p className="stn-item-desc">{row.description || 'No description'}</p>
            {asking === row.id ? (
              <div className="stn-confirm" role="group" aria-label={`Remove ${row.name}?`}>
                <p>Remove {row.name}? It disappears from every list; tickets already filed under it keep it.</p>
                <div className="stn-confirm-actions">
                  <button type="button" className="btn btn-small btn-danger" onClick={() => remove(row)}>Remove</button>
                  <button type="button" className="btn btn-small btn-secondary" onClick={() => setAsking(null)}>Keep it</button>
                </div>
              </div>
            ) : (
              <div className="stn-row-actions">
                <button type="button" className="btn btn-small btn-secondary" aria-label={`Edit ${row.name}`}
                        onClick={() => { setEditing(row); setForm({ name: row.name || '', description: row.description || '' }); }}>
                  Edit
                </button>
                <button type="button" className="btn btn-small btn-danger-outline" aria-label={`Remove ${row.name}`}
                        onClick={() => setAsking(row.id)}>
                  Remove
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DepartmentsPage() {
  return <NamedList path="departments" noun="department" nouns="Departments" placeholder="e.g. Tax" />;
}

export function TicketCategoriesPage() {
  return <NamedList path="categories" noun="category" nouns="Ticket categories" placeholder="e.g. Hardware" />;
}

/**
 * Priorities & statuses: two read-only lists on one page. They are built into
 * Jarvis - the SLA rules, the stage tabs and the approvals all depend on
 * them by name - and nothing in Settings can change them yet. This used to
 * say "Contact your administrator to modify" to the administrator.
 */
export function PrioritiesStatusesPage() {
  const [priorities, setPriorities] = useState(null);
  const [statuses, setStatuses] = useState(null);
  useEffect(() => {
    axios.get(`${API}/priorities`).then((r) => setPriorities(r.data || [])).catch(() => setPriorities([]));
    axios.get(`${API}/statuses`).then((r) => setStatuses(r.data || [])).catch(() => setStatuses([]));
  }, []);
  return (
    <div className="stn-ref">
      <p className="stn-help">
        These are built into Jarvis: SLA rules, the ticket stages and approvals all rely on them. They
        cannot be renamed, added to or removed here yet.
      </p>
      <section id="s-priorities" aria-labelledby="stn-pri-h">
        <h3 className="stn-card-title" id="stn-pri-h">Priorities</h3>
        <ul className="stn-ref-list">
          {priorities === null && <li className="stn-quiet">Loading…</li>}
          {(priorities || []).map((p) => (
            <li key={p.id} className="stn-ref-item">
              <span className={`badge badge-${String(p.name || '').toLowerCase()}`}>{p.name}</span>
              <span className="stn-quiet">Level {p.level}</span>
            </li>
          ))}
        </ul>
      </section>
      <section id="s-statuses" aria-labelledby="stn-st-h">
        <h3 className="stn-card-title" id="stn-st-h">Statuses</h3>
        <ul className="stn-ref-list">
          {statuses === null && <li className="stn-quiet">Loading…</li>}
          {(statuses || []).map((st) => (
            <li key={st.id} className="stn-ref-item">
              <span className={`badge badge-${String(st.name || '').toLowerCase().replace(/\s+/g, '-')}`}>{st.name}</span>
              {st.color && (
                // The colour is the row's own (statuses.color), so it can only
                // be drawn from the data; the swatch says its value as well.
                <span className="stn-swatch-wrap">
                  <span className="stn-swatch" style={{ background: st.color }} aria-hidden="true" />
                  <span className="stn-quiet">{st.color}</span>
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
